import { ChangeDetectorRef, Injectable } from '@angular/core';
import { act, Actions, createEffect, ofType } from '@ngrx/effects';
import {
  map,
  catchError,
  tap,
  mergeMap,
  withLatestFrom,
  exhaustMap,
} from 'rxjs/operators';
import { of } from 'rxjs';
import * as fromAuthActions from '../actions/auth.actions';
import * as fromAccountActions from '../actions/account.actions';
import * as fromOrganizationActions from '../actions/organization.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { AuthService } from 'src/app/services/auth.service';
import { AlertService } from 'src/app/services/alert.service';
import { setLoadingSpinner } from '../actions/shared.actions';
import { ErrorService } from 'src/app/services/error.service';
import { getUserProfile } from '../actions/account.actions';
import { isPlatform, ModalController, NavController, ToastController } from '@ionic/angular';
import { OfflineStorageService } from 'src/app/services/offline-storage.service';
import { NavigationExtras } from '@angular/router';
import { getFCMToken } from '../selectors/shared.selector';
import { StorageKeys } from 'src/app/utils/storage-keys';
import { BiometricService } from 'src/app/services/biometric.service';
import { VerificationComponent } from 'src/app/shared/modals/verification/verification.component';
import { PasswordUpdateSuccessComponent } from 'src/app/shared/modals/password-update-success/password-update-success.component';
import { ErrorComponent } from 'src/app/shared/modals/error/error.component';
import { ResendSuccessComponent } from 'src/app/shared/modals/resend-success/resend-success.component';
import { RegistrationSuccessComponent } from 'src/app/shared/modals/registration-success/registration-success.component';
import { DynamicErrorComponent } from 'src/app/shared/modals/dynamic-error/dynamic-error.component';
import { DynamicSuccessComponent } from 'src/app/shared/modals/dynamic-success/dynamic-success.component';

@Injectable()
export class AuthEffects {

  logIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.logIn),
      exhaustMap((action) =>
        this.authService.logIn(action).pipe(
          map((response: any) => {
            console.log('logIn$', action);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const user = this.authService.formatUser(response?.details?.tokens, response?.details?.user);
            this.authService.setUserSession(user);
            if(action?.remember == true){
              console.log(response?.UserData);
              this.storage.setObject('rememberedUser', response?.details?.user);
            }
            return fromAuthActions.logInSuccess({ data: user, redirect: true });
          }),
          catchError((response) => {
            console.error('logIn$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(fromAuthActions.loginFailed({ data: error, payload: action}));
          })
        )
      )
    )
  );

  socialLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.socialLogin),
      withLatestFrom(this.store.select(getFCMToken), (action, fcmToken) => {
        return { ...action, device_token: fcmToken };
      }),
      exhaustMap((action) =>
        this.authService.socialLogin(action).pipe(
          map((response: any) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const user = this.authService.formatUser(response?.tokens, response?.user);
            this.authService.setUserSession(user);
            return fromAuthActions.logInSuccess({ data: user, redirect: true });
          }),
          catchError((response) => {
            console.error('socialLogin$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            if(error?.error == 'Account not found.'){
              this.navCtrl.navigateForward('/auth/sign-up');
            }
            return of(fromAuthActions.loginFailed({ data: error, payload: action}));
          })
        )
      )
    )
  );

  socialLoginV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.socialLoginV2),
      withLatestFrom(this.store.select(getFCMToken), (action, fcmToken) => {
        return { ...action, device_token: fcmToken };
      }),
      exhaustMap((action) =>
        this.authService.socialLoginV2(action?.data, action?.socialType).pipe(
          map((response: any) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            console.log({response})
            const user = this.authService.formatUser(response?.details?.tokens, response?.details?.user);
            this.authService.setUserSession(user);
            return fromAuthActions.logInSuccess({ data: user, redirect: true });
          }),
          catchError((response) => {
            console.error('socialLoginV2$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            if(error?.error == 'Account not found.'){
              this.navCtrl.navigateForward('/auth/sign-up');
            }
            return of(fromAuthActions.loginFailed({ data: error, payload: action}));
          })
        )
      )
    )
  );

  deviceLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.deviceLogin),
      exhaustMap((action) =>
        this.authService.deviceLogin(action.data).pipe(
          map((response) => {
            console.log('deviceLogin$', response)
            return fromAuthActions.deviceLoginSuccess({ data: response });
          })
        )
      )
    )
  );

  deviceLoginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.deviceLoginSuccess),
        tap(async(action) => {
          console.log('deviceLoginSuccess$', action.data);
        })
      ),
    { dispatch: false }
  );

  loginRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.logInSuccess),
        tap(async (action) => {
          console.log('loginRedirect$', action);
          if (action.redirect) {
            const userData = action?.data?.userData;
            this.offlineStorageService.setObject('userData', userData);
            this.offlineStorageService.removeItem('isModalOpened');
            this.store.dispatch(getUserProfile());
            const deviceToken = await this.offlineStorageService.getObject('deviceToken');
            const payload = {
              deviceToken
            }
            this.store.dispatch(fromAuthActions.deviceLogin({data: payload}));
            this.store.dispatch(fromOrganizationActions.getOrganization());
            this.store.dispatch(fromOrganizationActions.getUserOrganization());
            this.store.dispatch(fromOrganizationActions.getUserOrganizationPoints());
            this.store.dispatch(fromOrganizationActions.getUserPasses());
            this.store.dispatch(fromOrganizationActions.getBroadcastMessage());
            // this.offlineStorageService.getObject("messageBroadcasted").then(async(x) => {
            //   if(!x){
            //     this.store.dispatch(fromOrganizationActions.getBroadcastMessage());
            //     this.offlineStorageService.setObject("messageBroadcasted", true);
            //   }
            // });
            this.navCtrl.navigateRoot('home/bottom-tabs/dashboard');
          }
        })
      ),
    { dispatch: false }
  );

  loginFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.loginFailed),
        tap(async (action) => {
          console.log('loginFailed$', action);    
          if (action?.data?.status === 400 && action?.data?.error === "User is not confirmed.") {
            const username = action.payload?.username;
            this.authService.resendconfirmation({username}).toPromise().then(async (res) => {
              console.log(res);
              const modal = await this.modalController.create({
                component: VerificationComponent,
                componentProps: {
                  tranType: 'signup',
                  username
                }
              });
              await modal.present(); 
            }).catch(async (response) => {
              const error = {
                status: response.status,
                error: this.errorService.getErrorMessage(response)
              };

              const modal = await this.modalController.create({
                component: ErrorComponent,   
                cssClass: 'login-error-modal',
                componentProps: { error } 
              });
              await modal.present();
            });
          }
          else{
            const error = {
              status: action?.data?.status,
              error: action?.data?.error
            };

            const modal = await this.modalController.create({
              component: ErrorComponent,   
              cssClass: 'login-error-modal',
              componentProps: { error } 
            });
            await modal.present();
          }
        })
      ),
    { dispatch: false }
  );

  autoLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.autoLogin),
      withLatestFrom(this.store.select(getFCMToken), (action, fcmToken) => {
        return { ...action, device_token: fcmToken };
      }),
      mergeMap(async (action) => {
        const user = await this.authService.getUserSession();
        return fromAuthActions.logInSuccess({ data: user, redirect: user ? true : false });
      })
    ) 
  );

  logOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.autoLogout),
        tap(() => {
          this.authService.logout();
          this.navCtrl.navigateRoot('/');
        })
      ),
    { dispatch: false }
  );

  forgotPassword$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromAuthActions.forgotPassword),
      exhaustMap((action) => 
        this.authService.forgotPassword(action).pipe(
          map((response)=>{
            console.log(response);
            this.store.dispatch(setLoadingSpinner({status : false}));
            return fromAuthActions.forgotSuccess({
              data : {
                username : action.username
              }
            })
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            const error = {
              status : response.status,
              error : this.errorService.getErrorMessage(response)
            };
            return of(fromAuthActions.forgotFailed({data : error}))
          })
        )
      )
    )
  )

  forgotSuccess$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromAuthActions.forgotSuccess),
        tap(async (action) =>{
          console.log("forgotSuccess$",action.data);
          if(action.data){
            const navigationExtras : NavigationExtras = {
              queryParams : {
                username : action.data.username
              },
              replaceUrl : false
            }
            this.navCtrl.navigateForward('/auth/forgot-password-confirm', navigationExtras);
            const modal = await this.modalController.create({
              component: ResendSuccessComponent,
              cssClass: 'resend-success-modal',
            });
            modal.present();
            setTimeout(() => {
              modal.dismiss();
            }, 2000);
          }
        })
      ),
    { dispatch: false }
  )

  forgotFailed$ = createEffect(
    ()=> this.actions$.pipe(
        ofType(fromAuthActions.forgotFailed),
        tap(async (action) =>{
          console.log("forgotSuccess$",action.data);
          if(action.data){
            console.log('resendForgotOtpFailed$', action.data);          
            const modal = await this.modalController.create({
              component: ErrorComponent,   
              cssClass: 'login-error-modal',
              componentProps: { error: action.data } 
            });
            await modal.present();
          }
        })
      ),
    { dispatch: false }
  )

  // forgotFailed$ = createEffect(

  // );


  resendForgotOtp$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAuthActions.resendForgotOtp),
      exhaustMap((action)=>
        this.authService.forgotPassword(action).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({status : false}));
            return fromAuthActions.resendForgotOtpSuccess({
              data: {
                username: action.username,
              },
            });
          }),
          catchError((response) => {
            console.error('resendForgotOtp$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(fromAuthActions.resendForgotOtpFailed({ data: error }));
          })
        )
      )
    )
  )

  resendForgotOtpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.resendForgotOtpSuccess),
        tap(async (action) => {
          console.log('resendForgotOtpSuccess$', action.data);
          // if(action.data){
          //   const modal = await this.modalController.create({
          //     component: ResendSuccessComponent,
          //     cssClass: 'resend-success-modal',
          //   });
          //   modal.present();
          //   setTimeout(() => {
          //     modal.dismiss();
          //   }, 2000);
          // }
        })
      ),
    { dispatch: false }
  );

  resendForgotOtpFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.resendForgotOtpFailed),
        tap(async(action) => {
          // console.log('resendForgotOtpFailed$', action.data);          
          // const modal = await this.modalController.create({
          //   component: ErrorComponent,   
          //   cssClass: 'login-error-modal',
          //   componentProps: { error: action.data } 
          // });
          // await modal.present();
        })
      ),
    { dispatch: false }
  );


  confirmPassword$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromAuthActions.confirmPassword),
      exhaustMap((action)=>
        this.authService.forgotPasswordConfirm(action.data).pipe(
          map((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return fromAuthActions.confirmSuccess({data : response})
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({status : false}));
            const error = {
              status : response.status,
              error : this.errorService.getErrorMessage(response)
            }
            return of(fromAuthActions.confirmFailed({data : error}))
          })
        )
      )
    )
  )

  confirmSuccess$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromAuthActions.confirmSuccess),
      tap(async (action)=>{
        const modal = await this.modalController.create({
          component : PasswordUpdateSuccessComponent,
          cssClass : 'password-update-success'
        })
        console.log("Confirm success enter");

        modal.onDidDismiss().then((res) => {
          this.navCtrl.navigateBack('/auth/sign-in');
        });
        return modal.present();

      })
    ),
    {dispatch : false}
  );

  confirmFailed$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromAuthActions.confirmFailed),
      tap(async (action) => {
        const modal = await this.modalController.create({
          component: ErrorComponent,   
          cssClass: 'login-error-modal',
          componentProps: { error: action.data } 
        });
        await modal.present();
      })
    ),
    {dispatch : false}
  );

  signUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.signUp),
      exhaustMap((action) =>
        this.authService.signUpV2(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAuthActions.signUpSuccess({ data: response?.details });
          }),
          catchError((response) => {
            console.error('signUp$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            // const error = {
            //   status: response.status,
            //   error: this.errorService.getErrorMessage(response)
            // };
            // console.log(error);

            
            this.failedModal("Error", response?.error?.message);
            return of(fromAuthActions.signUpFailed({ data: response?.error?.message }));
          })
        )
      )
    )
  );

  signUpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.signUpSuccess),
        tap(async (action) => {
          if(action?.data){
            console.log('signUpSuccess$', action?.data);
            this.registrationSuccessModal();
            this.navCtrl.navigateBack('auth/sign-in');
          }
        })
      ),
    { dispatch: false }
  );

  verifySignup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.verifySignUp),
      exhaustMap((action) =>
        this.authService.confirmRegistration(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            this.registrationSuccessModal();
            this.modalController.dismiss();
            return fromAuthActions.verifySignUpSuccess({ data: response });
          }),
          catchError((response) => {
            console.error('verifySignup$', response);
            this.store.dispatch(setLoadingSpinner({ status: false }));
            const error = {
              status: response.status,
              error: this.errorService.getErrorMessage(response)
            };
            return of(fromAuthActions.verifySignUpFailed({ data: error }));
          })
        )
      )
    )
  );

  verifySignupSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.verifySignUpSuccess),
        tap(async (action) => {
          if(action?.data){
            this.navCtrl.navigateBack('auth/sign-in');
          }
        })
      ),
    { dispatch: false }
  );

  async registrationSuccessModal(){
    const modal = await this.modalController.create({
      component: RegistrationSuccessComponent,
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false
    });

    return modal.present();
  }

  async failedModal(title: string, content: string){
    console.log('failed modal')
    const modal = await this.modalController.create({
      component: DynamicErrorComponent,
      componentProps: {
        title: title,
        content: content
      },
      cssClass: 'delete-account-confirm',
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      initialBreakpoint: 1,
      breakpoints: [0, 0.5, 1],
      handle: false
    });

    return modal.present();
  }



  signUpWeb$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.signUpWeb),
      exhaustMap((action) =>
        this.authService.signUpWeb(action.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));

            return fromAuthActions.signUpWebSuccess({ data: response?.details });
          }),
          catchError((response) => {
            console.error('signUp$', response);

            this.store.dispatch(setLoadingSpinner({ status: false }));

            this.failedModal("Error", response?.error?.message);
            return of(fromAuthActions.signUpWebFailed({ data: response?.error?.message }));
          })
        )
      )
    )
  );

  signUpWebSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.signUpWebSuccess),
        tap(async (action) => {
          if(action?.data){
            console.log('signUpSuccess$', action?.data);
            this.registrationSuccessModal();
            this.navCtrl.navigateBack('auth/sign-in');
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private navCtrl: NavController,
    private store: Store<AppState>,
    private alertService: AlertService,
    private errorService: ErrorService,
    private modalController: ModalController,
    private storage: OfflineStorageService,
    private toastController: ToastController,
    private offlineStorageService: OfflineStorageService,
    private storageKeys: StorageKeys,
    private biometricService: BiometricService
  ) {}
}
